import React, { useEffect, useState } from 'react';
import { Images, Loader2, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

export function ArtworkLibrary({ open, onOpenChange, kind, onPick }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(kind || 'all');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/artworks', { params: filter === 'all' ? {} : { kind: filter } }).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [open, filter]);

  const remove = async (e, it) => {
    e.stopPropagation();
    try { await api.delete(`/artworks/${it.id}`); setItems((s) => s.filter((x) => x.id !== it.id)); toast({ title: 'Artwork deleted' }); }
    catch (err) { toast({ title: 'Could not delete', description: err?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="artwork-library-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Images className="w-5 h-5 text-fuchsia-400" /> Artwork Library</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          {['all', 'illustration', 'background'].map((k) => <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full border capitalize ${filter === k ? 'border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-200' : 'border-white/10 text-slate-400 hover:text-slate-200'}`} data-testid={`artwork-filter-${k}`}>{k === 'all' ? 'All artwork' : k + 's'}</button>)}
          <span className="ml-auto text-slate-500">{items.length} saved · shared across the workspace</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto mt-2">
          {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div>
            : items.length === 0 ? <p className="py-16 text-center text-sm text-slate-500">No artwork yet. Generate one and it will be saved here automatically.</p>
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {items.map((it) => (
                  <button key={it.id} onClick={() => { onPick(it); onOpenChange(false); }} className="group relative rounded-xl border border-white/10 overflow-hidden text-left hover:border-fuchsia-400/60 transition-colors" style={{ background: 'repeating-conic-gradient(#1e293b 0% 25%, #0f172a 0% 50%) 50% / 16px 16px' }} data-testid={`artwork-item-${it.id}`}>
                    <img src={it.image} alt={it.subject} className="w-full aspect-square object-contain p-2" loading="lazy" />
                    <div className="px-2 py-1.5 text-[11px] text-slate-300 bg-[#0b1220] truncate" title={it.subject}>{it.subject}</div>
                    <span className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/60 text-slate-300">{it.kind}</span>
                    <span onClick={(e) => remove(e, it)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-red-300 hidden group-hover:flex items-center justify-center" title="Delete" data-testid={`artwork-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></span>
                  </button>
                ))}
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
